import { connect, useDispatch } from 'react-redux';
import { useCallback, useRef } from 'react';
import { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import { ActionType } from 'typesafe-actions';

import { IKnmAttachmentDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmAttachmentDocument';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { fetchPmCancelWarning } from 'APIPackage/KnmModule/services/knmApi';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmErknmPositionsSelectOptions, getKnmId } from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  knmReload,
  knmSign,
  knmSignWithSaveSigner,
  knmTryOpenSignModal,
} from 'KnmPackage/MainModule/actions/knmActions';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { knmPmReloadParticipantByCancel } from 'KnmPmPackage/MainModule/actions/knmPmActions';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './PmOpCloseWarningModal.module.scss';
import { WarningCancelDocument } from './WarningCancelDocument';
import { FieldMark } from '../FieldMark/FieldMark';

interface IPmOpCloseWarningModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IPmOpCloseWarningModalStoreProps {
  knmId: number | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
}

interface IForm {
  comment: string;
  attachment: IKnmAttachmentDocument | undefined;
  officialFullNameCancel: string;
  warningContentPositionCancel:
    | IDictionaryPositionSelectOption
    | IDictionaryParticipantPositionSelectOption
    | undefined;
}

const PmOpCloseWarningModalWithoutStore = ({
  visible,
  onClose,
  knmId,
  positionsSelectOptions,
}: IPmOpCloseWarningModalProps & IPmOpCloseWarningModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRef = useRef<FormApi<IForm, Partial<IForm>>>();

  const validateForm = useCallback((values: IForm) => {
    return {
      comment: makeRequired('Поле не может быть пустым')(values.comment),
      attachment: makeRequired('Поле не может быть пустым')(values.attachment),
      officialFullNameCancel: makeRequired('Поле не может быть пустым')(values.officialFullNameCancel),
      warningContentPositionCancel: makeRequired('Поле не может быть пустым')(values.warningContentPositionCancel),
    };
  }, []);

  const handleClose = () => {
    onClose();
    formRef?.current?.reset();
  };

  const handleCancel = useCallback(
    async (values: IForm) => {
      const data = {
        knmId,
        ...values,
        warningContentPositionCancel: values.warningContentPositionCancel?.value,
      };

      dispatch(
        knmTryOpenSignModal(
          undefined,
          undefined,
          true,
          undefined,
          undefined,
          (action: ActionType<typeof knmSign> | ActionType<typeof knmSignWithSaveSigner>) => {
            dispatch(coreSetLoading(true));

            fetchPmCancelWarning(data)
              .then(() => {
                const callback = async () => {
                  dispatch(coreSetLoading(false));
                  dispatch(action);
                  handleClose();
                  dispatch(knmPmReloadParticipantByCancel());
                };

                dispatch(knmReload(callback));
              })
              .catch(() => {
                dispatch(coreSetLoading(false));
                handleClose();
              });
          },
          true,
          true,
          data.officialFullNameCancel,
        ),
      );
    },
    [knmId],
  );

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values }: FormRenderProps<IForm>) => {
      formRef.current = form;

      return (
        <>
          <ModalBody size="content">
            <div className={styles.ContentWrapper}>
              <FieldBlock
                label={<FieldMark fieldName="comment" label="Комментарий" />}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  name={`comment`}
                  size="medium"
                  resize="vertical"
                  inputClassName={styles.CopyReasonTextarea}
                  autoHeight={true}
                />
              </FieldBlock>
              <WarningCancelDocument
                values={values.attachment ? [values.attachment] : []}
                addAttachment={data => form.change('attachment', data)}
                removeField={() => form.change('attachment')}
              />
              <Row>
                <FieldBlock
                  label={<FieldMark fieldName="officialFullNameCancel" label="ФИО должностного лица" />}
                  color="black"
                  size="half"
                >
                  <TextInputField name={`officialFullNameCancel`} />
                </FieldBlock>
                <FieldBlock
                  label={
                    <FieldMark
                      fieldName="warningContentPositionCancel"
                      label="Должность лица, отменившего предостережение"
                    />
                  }
                  color="black"
                  size="half"
                >
                  <div className={styles.PositionField}>
                    <SelectInputField
                      name={`warningContentPositionCancel`}
                      placeholder="Выберите должность"
                      options={positionsSelectOptions}
                      isSearchable
                      size="medium"
                      opensOnTop
                    />
                    <Hint text="Должность лица уполномоченного на принятие решения (из ЕРВК)" />
                  </div>
                </FieldBlock>
              </Row>
            </div>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [positionsSelectOptions, handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader title="Отмена предостережения" />
      <Form<IForm> render={renderContentForm} onSubmit={handleCancel} validate={validateForm} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IPmOpCloseWarningModalStoreProps => ({
  knmId: getKnmId(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
});

export const PmOpCloseWarningModal = connect(mapStateToProps)(PmOpCloseWarningModalWithoutStore);
